'use client'
import * as Dialog from 'primitives/dialog'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { tw } from 'utils/classnames'
import { CrossIcon } from 'primitives/icons'
import { useQueryClient } from '@tanstack/react-query'
import { SkeletonItem } from 'components/skeleton-item'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'

import { LFS_URL } from '@web/utils/env'
import { useClubhousesGetDetailsQuery, useClubhousesJoinMutation } from '@web/gen/clubhouses'
import { formatString } from '../../../_utils/format-string'
import {
  CompassIcon,
  HandShakeIcon,
  FavoriteOutlineIcon,
  LightBulbIcon,
} from '../../../assets/icons'
import { buttonClassPrimary } from '../common-styles'

export function JoinClubhouseModal({ slug, onClose }: { slug: string; onClose: () => void }) {
  const t = useTranslations('pages.clubhouse')
  const clubhouseName = formatString(slug)
  const joinMutation = useClubhousesJoinMutation()
  const queryClient = useQueryClient()
  const user = useUser()
  const isLoggedInQuery = useIsLoggedInQuery()
  const { openModal } = useUserAuthModal()

  const { data: clubhouse, isPending } = useClubhousesGetDetailsQuery(
    {
      input: {
        clubhouseSlug: slug,
      },
    },
    {
      select: data => data.clubhousesGetDetails.clubhouse,
    },
  )

  const joinClubhouse = async () => {
    await joinMutation.mutateAsync(
      { input: { clubhouseSlug: slug } },
      {
        onSuccess: data => {
          if (data?.clubhousesJoin?.Success) {
            sendAmplitudeEvent(AmplitudeEvents.JOINED_CLUBHOUSE, {
              title: clubhouse?.name,
            })
            queryClient.invalidateQueries({
              queryKey: ['clubhousesIsMember', { input: { clubhouseSlug: slug } }],
            })
            if (user.data?.id) {
              queryClient.invalidateQueries({
                queryKey: ['clubhousesFromUser', { input: { userId: user.data.id } }],
              })
            }
            onClose()
          }
        },
      },
    )
  }

  // oxlint-disable-next-line require-await
  const handleClick = async () => {
    if (!isLoggedInQuery.data) {
      openModal('register', {
        successCallback: () => {
          joinClubhouse()
        },
      })
      return
    }
    joinClubhouse()
  }

  return (
    <div
      data-testid="join-clubhouse-modal"
      className="bg-cf-white relative flex min-h-[100dvh] flex-col justify-between md:block md:h-auto md:min-h-fit md:w-[658px] md:rounded-[12px]"
    >
      <div>
        <div
          style={{
            background: `linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 60%), ${clubhouse?.lightBgColor}`,
            backgroundBlendMode: 'overlay, normal',
            '--bgColor': clubhouse?.bgColor || 'bg-[#C0D8E4]',
          }}
          className={tw(
            'md:rounded-t-2 flex h-[172px] items-center justify-center',
            `bg-[--bgColor]`,
          )}
        >
          {isPending ? (
            <SkeletonItem className="h-[172px] w-full" />
          ) : (
            <Image
              src={clubhouse?.bannerImage || `${LFS_URL}/web/pages/clubhouses/clubhouse-cover.png`}
              alt={clubhouseName}
              width={400}
              height={172}
              className="h-auto w-auto max-w-[400px]"
            />
          )}
        </div>
        <div className="p-4 md:p-8">
          <h2 className="text-20 pb-1 font-bold">
            {t('topbanner.join_dialog_title', { clubhouse: clubhouseName })}
          </h2>
          <p className="text-16">
            {t('topbanner.join_dialog_subtitle', { clubhouse: clubhouseName })}
          </p>
          <div className="flex flex-col gap-3 py-6">
            <div className="flex gap-2">
              <CompassIcon className="size-5 text-[#59B89D]" />
              <p>{t('topbanner.benefits_1', { clubhouse: clubhouseName })}</p>
            </div>
            <div className="flex gap-2">
              <HandShakeIcon className="size-5 text-[#59B89D]" />
              <p>{t('topbanner.benefits_2')}</p>
            </div>
            <div className="flex gap-2">
              <FavoriteOutlineIcon className="size-5 text-[#59B89D]" />
              <p>{t('topbanner.benefits_3')}</p>
            </div>
            <div className="flex gap-2">
              <LightBulbIcon className="size-5 text-[#59B89D]" />
              <p>{t('topbanner.benefits_4', { clubhouse: clubhouseName })}</p>
            </div>
          </div>
          <div className="hidden justify-end gap-4 md:flex">
            <Dialog.Close asChild>
              <button className="border-0 bg-transparent p-2.5 outline-none focus-visible:outline-none">
                {t('topbanner.close_cta')}
              </button>
            </Dialog.Close>
            <button
              onClick={handleClick}
              className={tw(buttonClassPrimary, 'mb-1 text-center')}
              data-testid="clubhouse-dialog-join-button"
            >
              {t('topbanner.cta')}
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-col border-t border-[#0A0A0B1A] p-4 pt-5 md:hidden">
        <button onClick={handleClick} className={tw(buttonClassPrimary, 'mb-1 text-center')}>
          {t('topbanner.cta')}
        </button>
        <Dialog.Close asChild>
          <button className="border-0 bg-transparent p-2.5 outline-none focus-visible:outline-none">
            {t('topbanner.close_cta')}
          </button>
        </Dialog.Close>
      </div>
      <Dialog.Close className="absolute right-4 top-4">
        <CrossIcon className="text-cf-sf-gray-1150 size-6" />
      </Dialog.Close>
    </div>
  )
}
